import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  closeImageDialog$,
  editorRootElementRef$,
  imageDialogState$,
  saveImage$,
  useCellValues,
  usePublisher,
} from "@mdxeditor/editor";
import { useForm } from "@tanstack/react-form";
import { useId } from "react";

type ImageFields = { src: string; altText: string; title: string };

/**
 * The dialog's fields for one image, saved through MDXEditor's image plugin.
 * Mounted per open, so its form always starts from that image's values.
 */
const ImageDialogForm = ({
  heading,
  initialValues,
  container,
}: {
  heading: string;
  initialValues: ImageFields;
  container: HTMLElement;
}) => {
  const saveImage = usePublisher(saveImage$);
  const closeImageDialog = usePublisher(closeImageDialog$);
  const srcId = useId();
  const altId = useId();
  const titleId = useId();

  const form = useForm({
    defaultValues: initialValues,
    // Publishing the save also closes the dialog: the editor's image plugin
    // sets its dialog state back to inactive once the node is written.
    onSubmit: ({ value }) => saveImage({ ...value, src: value.src.trim() }),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && closeImageDialog()}>
      {/* `font-sans` because the container carries the editor library's own
          font; without it the dialog is the one surface not in the app's. */}
      <DialogContent container={container} aria-describedby={undefined} className="font-sans">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // The dialog sits inside whatever form hosts the editor in the React
            // tree, and a portal does not stop a submit event from bubbling there.
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="space-y-3"
        >
          <form.Field name="src">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor={srcId}>Image address</Label>
                <Input
                  id={srcId}
                  required
                  autoComplete="off"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>
          <form.Field name="altText">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor={altId}>Description</Label>
                <Input
                  id={altId}
                  autoComplete="off"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <p className="text-xs text-muted-foreground">
                  Read aloud by screen readers and shown if the image can't load.
                </p>
              </div>
            )}
          </form.Field>
          <form.Field name="title">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor={titleId}>Title</Label>
                <Input
                  id={titleId}
                  autoComplete="off"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghostPopover"
                size="sm"
                data-statsig-name="artifact-editor-image-cancel"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              data-statsig-name="artifact-editor-image-save"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The image settings dialog for `ArtifactEditor`, in the app's own dialog,
 * inputs and buttons. Replaces the one MDXEditor's image plugin ships, which
 * is styled by the library's stylesheet and matches nothing else in the app.
 *
 * Reads and writes the plugin's own dialog state, so opening it (the settings
 * button on an image) and what Save does to the image node are unchanged.
 *
 * It portals into the editor's popup container, as the built-in one does.
 * `ArtifactEditor` treats focus moving there as still inside the editor; in
 * `document.body` the dialog opening would count as a blur and save mid-edit.
 */
export const ArtifactImageDialog = () => {
  const [state, editorRootElementRef] = useCellValues(imageDialogState$, editorRootElementRef$);
  const container = editorRootElementRef?.current;

  // No container means no dialog rather than one in `document.body`, where
  // opening it would be read as a blur. The dialog is opened from a mounted
  // editor, which is what sets the container, so this does not hide it in use.
  if (state.type === "inactive" || !container) return null;

  const editing = state.type === "editing";
  const initialValues: ImageFields = {
    src: (editing && state.initialValues.src) || "",
    altText: (editing && state.initialValues.altText) || "",
    title: (editing && state.initialValues.title) || "",
  };

  return (
    <ImageDialogForm
      // A fresh form per image, so one image's fields never open on another.
      key={editing ? state.nodeKey : "new"}
      heading={editing ? "Edit image" : "Add image"}
      initialValues={initialValues}
      container={container}
    />
  );
};
